import { BellOutlined, LeftOutlined } from '@ant-design/icons'
import { DeviceType } from '@services/constants'
import {
  Device,
  IStatisticalByDeviceType,
  sumStatisticalCountByDeviceType,
} from '@services/device'
import { ISession, useAllSessions } from '@services/session'
import { useMobilePageHeaderSetter } from '@shared/innmaxLib/hooks/useMobilePageHeader'
import useModal from '@shared/innmaxLib/hooks/useModal'
import InnmaxMap2, { InnmaxMarker } from '@shared/InnmaxMap2'
import useWatchMap from '@shared/InnmaxMap2/hooks/useWatchMap'
import { Layout, Loading } from '@shared/innmaxUI'
import { LoadingBar } from '@shared/innmaxUI/Loading'
import VideoPlayer from '@shared/innmaxUI/VideoPlayer'
import { flatten, isEmpty, isNil, path, pathOr } from 'ramda'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { useUrlSeach } from 'src/pages/MapDashboard/useUrlSearch'
import styled, { css } from 'styled-components'
import { convertToDeviceCountInfoWithFilter } from '../Desktop/MapDashboard'
import { MapDashboardProvider, useDashboardContext } from '../Desktop/Provider'
import AlarmDrawer from './AlarmDrawer'
import ParkingLotsView from './ParkingLotsView'
import SimpleDevicesBoard from './SimpleDeviceBoard'

const InternalMapDashboard = () => {
  const {
    currentSession,
    sessionDevices,
    setCurrentSession,
    sessionsById,
    mapInstance,
    videoPlayer,
    closeVideoPlayer,
    isFilterActived,
    lastUpdated,
    filterVariables,
    loadingDevices,
    deviceCountInfoBySessionId,
    isLoadDeviceCountInfo,
    getSessionDeepStatistical,
    baseLayer,
    parkingLotViewLoading,
    viewMode,
  } = useDashboardContext()

  const { setShowShadow, setActionNodes, setTitle } =
    useMobilePageHeaderSetter()

  React.useEffect(() => {
    if (currentSession?.id === baseLayer?.id) {
      setTitle(<span className="text-primary text-xl">圖資監控</span>)
      return
    }
    setTitle(
      <div className="flex gap-15 items-center text-primary text-xl">
        <LeftOutlined
          onClick={() => {
            baseLayer && setCurrentSession?.(baseLayer)
          }}
        />
        <span>{currentSession?.name}</span>
      </div>
    )
  }, [currentSession])

  const {
    visible: isAlarmDrawerOpen,
    open: openAlarmDrawer,
    close: closeAlarmDrawer,
  } = useModal()

  React.useLayoutEffect(() => {
    setShowShadow(true)
    setActionNodes([
      {
        key: 'alarms',
        element: (
          <BellOutlined
            className="text-primary text-2xl"
            onClick={() => openAlarmDrawer()}
          />
        ),
      },
    ])
  }, [])

  const history = useHistory()

  useUrlSeach({
    searchDeviceOnSuccess: (session: ISession, device: Device) => {
      setCurrentSession(session)
      mapInstance?.setFocusDevice(device)

      history.replace(history.location.pathname)
    },
    searchDevicesOnSuccess: (data: Device[]) => {
      const _s: ISession[] = data.map(i => sessionsById[i.sessionId])
      if (_s && Array.isArray(_s)) {
        setCurrentSession(_s[0])
        mapInstance?.setSelectedDevices(data)
      }
      history.replace(history.location.pathname)
    },
    searchSessionOnSuccess: (session: ISession) => {
      setTimeout(() => {
        setCurrentSession(session)
      }, 0)
      history.replace(history.location.pathname)
    },
  })

  const closeMarkerSidebar = React.useCallback(() => {
    mapInstance?.setFocusDevice(undefined)
    mapInstance?.setSelectedDevices([])
  }, []) // eslint-disable-line

  const SessionMarkers = React.useMemo(() => {
    if (isLoadDeviceCountInfo) {
      return []
    }
    const recrusiveForChildren = (session: any) => {
      if (session.children.length === 0) {
        return [session.id]
      }
      return session.children.map((child: any) => {
        return recrusiveForChildren(child)
      })
    }

    const children = pathOr([], ['children'], currentSession)

    return children.map((session: any) => {
      const allSessionIds = flatten(recrusiveForChildren(session))
      const statisticalByDeviceType = deviceCountInfoBySessionId

      const deviceCountInfo = flatten(
        allSessionIds.map((id: number) => path([id], statisticalByDeviceType))
      ) as IStatisticalByDeviceType[]

      const deviceType = filterVariables?.deviceType || DeviceType.CONNECTOR
      const statistical = sumStatisticalCountByDeviceType(
        deviceCountInfo,
        deviceType
      )
      const deviceCount = statistical.connectorCount
      const deviceChargingCount = statistical.summaryChargingCount
      const deviceUnavailableCount = statistical.summaryUnavailableCount
      const deviceAvailableCount = statistical.summaryAvailableCount

      if (isFilterActived) {
        if (deviceCount === 0) {
          return null
        }
        if (filterVariables?.available && deviceAvailableCount === 0) {
          return null
        }
        if (filterVariables?.charging && deviceChargingCount === 0) {
          return null
        }
        if (filterVariables?.unavailable && deviceUnavailableCount === 0) {
          return null
        }
      }
      return (
        <InnmaxMarker.Session
          key={session.id}
          session={session}
          deviceCountInfo={convertToDeviceCountInfoWithFilter(filterVariables, {
            deviceCount,
            deviceAvailableCount,
            deviceChargingCount,
            deviceUnavailableCount,
          })}
          statistical={deviceCountInfo}
          onLeftClick={(session: any) => {
            closeMarkerSidebar()
            setCurrentSession(session)
          }}
        />
      )
    })
  }, [
    currentSession,
    isLoadDeviceCountInfo,
    isFilterActived,
    filterVariables,
    deviceCountInfoBySessionId,
    getSessionDeepStatistical,
  ])

  const Markers = React.useMemo(() => {
    return <>{SessionMarkers}</>
  }, [SessionMarkers])

  const { getFullSessions } = useAllSessions()

  const focusParkingLot = useWatchMap(mapInstance, 'focusParkingLot')
  const focusDevice = useWatchMap(mapInstance, 'focusDevice')

  const renderLoading = () => {
    if (isLoadDeviceCountInfo || loadingDevices || parkingLotViewLoading) {
      return <LoadingBar />
    }
    return null
  }

  const renderMapView = () => {
    if (!currentSession || !baseLayer) {
      return (
        <div>
          <Loading />
        </div>
      )
    }
    if (viewMode === 'map') {
      return (
        <InnmaxMap2
          mapInstance={mapInstance}
          mapType={currentSession?.type}
          layerFileUrl={currentSession?.layerFileUrl}
          mapContainerStyle={{
            height: 'calc(100vh - 64px)',
          }}
          defaultCenter={{
            lat: currentSession.lat,
            lng: currentSession.lon,
          }}
          onRectangleComplete={rectangle => {
            const selectedDevices = sessionDevices?.filter(x =>
              rectangle
                .getBounds()
                ?.contains({ lat: x.lat as number, lng: x.lon as number })
            )
            mapInstance.setSelectedDevices(selectedDevices)
            mapInstance.setFocusDevice(path([0], selectedDevices))
          }}
          defaultZoom={8}
          googleMapOptions={{
            controlsOption: {
              zoomControlOptions: null,
              satelliteControlOption: undefined,
              streetViewControlOptions: null,
            },
          }}>
          {Markers}
        </InnmaxMap2>
      )
    }
    return <ParkingLotsView />
  }

  return (
    <Layout
      id="page-content"
      className="p-0"
      style={{ padding: 0, height: 'calc(100dvh - 50px)' }}>
      <MapLayout>
        {renderLoading()}
        {/* <MarkerSidebar /> */}

        {Object.keys(videoPlayer).map((id: string) => (
          <VideoPlayer
            key={id}
            isOpen
            onRequestClose={() => closeVideoPlayer(videoPlayer[id].device)}
            device={videoPlayer[id].device}
          />
        ))}
      </MapLayout>
      <div
        css={css`
          width: 100dvw;
        `}>
        {isNil(currentSession) || isEmpty(currentSession)
          ? null
          : renderMapView()}
      </div>
      <div className="absolute bottom-0 left-0 w-full h-120 bg-[#1B1D1D] z-10">
        <SimpleDevicesBoard lastUpdated={lastUpdated} />
      </div>
      <AlarmDrawer
        open={isAlarmDrawerOpen}
        onClose={() => closeAlarmDrawer()}
        lastUpdated={lastUpdated}
      />
    </Layout>
  )
}

const MapDashboard = () => {
  return (
    <MapDashboardProvider>
      <InternalMapDashboard />
    </MapDashboardProvider>
  )
}

const MapLayout = styled.div``

export default MapDashboard
